import { LOCALE, Locale } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { DEV_ROOTS } from "../devSettings";
import type { DevSettings } from "../devSettings";

interface MockServerConfigResponse {
  readonly root?: unknown;
  readonly tradename?: unknown;
  readonly locale?: unknown;
  readonly segment?: unknown;
  readonly customerId?: unknown;
}

interface LoadMockServerConfigArgs {
  readonly url: string;
  readonly fetch: (url: string) => Promise<Response>;
  readonly timeout?: number;
}

const CONFIG_TIMEOUT = 10000;

const isOneOf =
  <T>(values: readonly T[]) =>
  (value: unknown): value is T =>
    (values as readonly unknown[]).includes(value);

const isDevRoot = isOneOf(DEV_ROOTS);
const isTradename = isOneOf(Object.values(Tradename));
const isLocale = isOneOf(Object.values(Locale));
const isSegment = isOneOf(Object.values(Segment));

const reasonOf = (error: unknown): string => (error instanceof Error ? error.message : String(error));

const withTimeout = <T>(promise: Promise<T>, milliseconds: number, onTimeout: () => Error): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(onTimeout()), milliseconds);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

const loadMockServerConfig = async ({
  url,
  fetch,
  timeout = CONFIG_TIMEOUT,
}: LoadMockServerConfigArgs): Promise<DevSettings> => {
  const configError = (reason: string): Error => new Error(`Native test variant: GET ${url} ${reason}`);

  const request = async (): Promise<MockServerConfigResponse> => {
    const response = await fetch(url).catch((error: unknown) => {
      throw configError(`failed: ${reasonOf(error)}`);
    });

    if (!response.ok) {
      throw configError(`responded ${response.status}`);
    }

    return (await response.json().catch(() => {
      throw configError("returned invalid JSON");
    })) as MockServerConfigResponse;
  };

  const config = await withTimeout(request(), timeout, () => configError(`timed out after ${timeout}ms`));
  const { root, tradename, locale, segment, customerId } = config;

  if (
    !isDevRoot(root) ||
    !isTradename(tradename) ||
    !isLocale(locale) ||
    !isSegment(segment) ||
    (customerId !== undefined && typeof customerId !== "string")
  ) {
    throw configError(`returned invalid settings: ${JSON.stringify(config)}`);
  }

  return { root, tradename, locale, country: LOCALE[locale][1], segment, customerId };
};

export { CONFIG_TIMEOUT, loadMockServerConfig };
export type { LoadMockServerConfigArgs };
